import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DataTable, Dialog, Loader, useDialog } from 'components';
import { useAuth } from 'contexts';
import { getGateTransactionsById, getUnit, getUnitActivity } from 'services';
import { ActiveUnit, BillableEvent, EdiCustomerLocation, Role, Transaction, UnitActivityDisplay } from 'types';
import { transformUnitActivity } from 'utils';

import ErrorDialog from 'components/organization/dialog/error-dialog';
import SuccessDialog from 'components/organization/dialog/success-dialog';
import { useCustomerAPI } from 'hooks/use-customer-api';
import { useUnitApi } from 'hooks/use-unit-api';
import AssociatedUnits from './associated-units';
import BillableEventDialog from './billable-event-dialog';
import BillableEventsList from './billable-events-list';
import EquipmentDetails from './equipment-details';
import GeneralInformation from './general';
import { ResendUnitEdiDialog } from './resend-unit-edi-dialog';
import { unitActivityDataColumns } from './unit-activity-columns';
import './unit-details.scss';
import UnitInvoice from './unit-invoice';
import UnitTransactions from './unit-transactions';
import UpdateUnitStatus from './update-unit-status';

/** Roles that edit a unit and add lift events (the API's unit edit and lift allowlists). */
const UNIT_EDIT_ROLES = [Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING, Role.GATE_BOOKING];

const UnitDetails = () => {
  // CONTEXT
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { getCustomerById } = useCustomerAPI();
  const { canTransferUnit } = useUnitApi();
  const unitId = useMemo(() => Number(params.unitId), [params.unitId]);

  // STATE
  const [loading, setLoading] = useState(false);
  const [unit, setUnit] = useState<ActiveUnit | null>(null);
  const [gateInTransaction, setGateInTransaction] = useState<Transaction>();
  const [gateOutTransaction, setGateOutTransaction] = useState<Transaction>();
  const [billableEventToEdit, setBillableEventToEdit] = useState<BillableEvent | null>(null);
  const [billableEventOpenDialog, setBillableEventOpenDialog] = useState(false);
  const [ediCustomerLocations, setEdiCustomerLocations] = useState<EdiCustomerLocation[]>([]);
  const [resendEdiOpenDialog, setResendEdiOpenDialog] = useState(false);
  const [eventDialogKey, setEventDialogKey] = useState(Math.random());
  const [assocUnitsListKey, setAssocUnitsListKey] = useState(Math.random());
  const [billableEventsListKey, setBillableEventsListKey] = useState(Math.random());
  const [hasBeenTransferred, setHasBeenTransfered] = useState<boolean>();
  // used to reload Unit Invoices
  const [unitInvoicesRefresh, setUnitInvoicesRefresh] = useState(Math.random());

  const {
    ref: viewActivityDialogRef,
    openDialog: openViewActivityDialog,
    closeDialog: closeViewActivityDialog
  } = useDialog({});

  // CALLBACKS
  // Unit Activity
  const loadUnitActivities = useCallback(async () => {
    if (isNaN(unitId)) {
      return [];
    }

    const activities = await getUnitActivity(unitId);
    const transforms: UnitActivityDisplay[] = [];

    for (const [index, activity] of activities.entries()) {
      transforms.push(
        index > 0
          ? transformUnitActivity(activity, unit?.location?.name, activities.at(index - 1), activities.at(index + 1))
          : transformUnitActivity(activity, unit?.location?.name, undefined, activities[1])
      );
    }

    return transforms.reverse();
  }, [unit?.location?.name, unitId]);

  // Create Billable Event in dialog
  const createBillableEvent = useCallback(() => {
    setBillableEventToEdit(null);
    setBillableEventOpenDialog(true);
  }, []);

  const editBillableEvent = useCallback((billableEvent: BillableEvent) => {
    setBillableEventToEdit({ ...billableEvent });
    setBillableEventOpenDialog(true);
  }, []);

  const reloadBillableEventsAndAssociatedUnitsAndInvoices = () => {
    setBillableEventsListKey(Math.random());
    setAssocUnitsListKey(Math.random());
    setUnitInvoicesRefresh(Math.random());
  };

  const loadUnit = useCallback(async () => {
    if (isNaN(unitId)) return;
    setLoading(true);
    const unit = await getUnit(unitId);
    if (unit !== null) {
      getGateTransactionsById(unitId).then(res => {
        setGateInTransaction(res[0]);
        setGateOutTransaction(res[1]);
      });
    }
    setUnit(unit);
    if ([Role.ADMIN, Role.BILLING_ADMIN, Role.BOOKING].includes(userProfile.role)) {
      setHasBeenTransfered(!(await canTransferUnit(unitId)));
    }
    setLoading(false);
  }, [canTransferUnit, unitId, userProfile.role]);

  // EFFECTS
  // Load Unit
  useEffect(() => {
    loadUnit();
  }, [loadUnit]);

  /* load EDI recipients */
  useEffect(() => {
    if (!!unit && !!unit.customer) {
      getCustomerById(unit.customer.id).then(customer => {
        if (!!customer) {
          setEdiCustomerLocations([...customer.ediRecipients]);
        }
      });
    }
  }, [getCustomerById, unit]);

  return (
    <>
      {UNIT_EDIT_ROLES.includes(userProfile.role) && !!unit && (
        <BillableEventDialog
          key={eventDialogKey}
          unit={unit}
          existingBillableEvent={billableEventToEdit}
          open={billableEventOpenDialog}
          close={() => {
            setBillableEventOpenDialog(false);
            setEventDialogKey(Math.random());
          }}
          onSuccess={reloadBillableEventsAndAssociatedUnitsAndInvoices}
        />
      )}
      {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) && !!unit && (
        <Dialog width="80vw" ref={viewActivityDialogRef} closeDialog={closeViewActivityDialog}>
          <div className="dialog-header">
            <h3>Activity Log</h3>
            <button className="btn-close" onClick={closeViewActivityDialog}>
              <span className="material-icons">close</span>
            </button>
          </div>
          <div className="dialog-body">
            <DataTable
              tableClass="activity-log"
              fetchData={loadUnitActivities}
              columns={unitActivityDataColumns}
              initialPage={{ pageNum: 0, pageSize: 25, sortField: 'date', sortDirection: 'desc' }}
              initialColumns={unitActivityDataColumns.map(({ field }) => {
                return { field, width: 80 };
              })}
              clientSidePagination
              hideTableControls
            />
          </div>
        </Dialog>
      )}

      <div className="page" id="unit-details">
        <div className="page-header">
          <div className="d-flex g-3">
            <h1>UNIT #{unit?.unitNumber ?? 'N/A'}</h1>
            {!unit?.active && !loading && (
              <div className="font-weight-bold px-3 py-2 text-white inactive-badge" data-cy="unit-inactive-badge">
                INACTIVE
              </div>
            )}
            {hasBeenTransferred && (
              <div className="font-weight-bold px-3 py-2 text-white inactive-badge" data-cy="unit-transferred-badge">
                TRANSFERRED
              </div>
            )}
          </div>
          <div className="d-flex g-2 flex-row-reverse flex-wrap header-buttons">
            {![Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) ? null : (
              <button
                className="btn-primary text-uppercase"
                onClick={openViewActivityDialog}
                data-cy="unit-view-log-button"
              >
                VIEW LOG
              </button>
            )}
            {!UNIT_EDIT_ROLES.includes(userProfile.role) ? null : (
              <button
                className="btn-primary text-uppercase"
                onClick={createBillableEvent}
                data-cy="unit-add-billable-event-button"
              >
                ADD BILLABLE EVENT
              </button>
            )}
            {!UNIT_EDIT_ROLES.includes(userProfile.role) ? null : (
              <UpdateUnitStatus loadUnit={loadUnit} {...unit} />
            )}
            {![
              Role.ADMIN,
              Role.BILLING_ADMIN,
              Role.BOOKING,
              Role.SENIOR_BOOKING,
              Role.ADMIN_CUSTOMER,
              Role.CUSTOMER,
              Role.EXTERNAL
            ].includes(userProfile.role) ? null : (
              <button
                className="btn-outline"
                onClick={() => setResendEdiOpenDialog(true)}
                data-cy="unit-resend-edi-button"
              >
                RESEND EDI
              </button>
            )}
            {![Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) ? null : (
              <button
                className="btn-outline"
                disabled={hasBeenTransferred !== false}
                onClick={() => navigate('transfer', { replace: true })}
                data-cy="unit-renumber-button"
              >
                RENUMBER
              </button>
            )}
            {!UNIT_EDIT_ROLES.includes(userProfile.role) ? null : (
              <button
                className="btn-outline"
                onClick={() => navigate('edit', { replace: true })}
                data-cy="unit-edit-button"
              >
                EDIT
              </button>
            )}
            <button className="btn-back" onClick={() => navigate(-1)}>
              <span className="material-icons">arrow_back_ios</span>
              BACK
            </button>
          </div>
        </div>
        <div className="page-content">
          <Loader loading={loading}>
            <GeneralInformation {...unit} />
            <AssociatedUnits key={assocUnitsListKey} />
            <EquipmentDetails {...unit} />
            <UnitTransactions unitNumber={unit?.unitNumber || undefined} />
            {[Role.ADMIN, Role.BILLING_ADMIN, Role.SENIOR_BOOKING, Role.BOOKING].includes(userProfile.role) &&
              !!unit && (
                <div className="card-primary">
                  <div className="card-header px-3">
                    <h4>BILLABLE EVENTS</h4>
                  </div>
                  <div className="card-body">
                    <BillableEventsList key={billableEventsListKey} unit={unit} rowClicked={editBillableEvent} />
                  </div>
                </div>
              )}
            {[Role.ADMIN, Role.BILLING_ADMIN].includes(userProfile.role) && !!unit && (
              <UnitInvoice
                unit={unit}
                gateInTransaction={gateInTransaction}
                gateOutTransaction={gateOutTransaction}
                refreshInvoices={unitInvoicesRefresh}
              />
            )}
            {[
              Role.ADMIN,
              Role.BILLING_ADMIN,
              Role.BOOKING,
              Role.SENIOR_BOOKING,
              Role.ADMIN_CUSTOMER,
              Role.CUSTOMER,
              Role.EXTERNAL
            ].includes(userProfile.role) &&
              !!unit &&
              resendEdiOpenDialog && (
                <ResendUnitEdiDialog
                  unit={unit}
                  ediCustomerLocations={ediCustomerLocations.filter(({ ediType }) =>
                    ['LIFT', 'STATUS'].includes(ediType)
                  )}
                  close={() => setResendEdiOpenDialog(false)}
                />
              )}
          </Loader>
        </div>
      </div>
      <SuccessDialog />
      <ErrorDialog />
    </>
  );
};

export default UnitDetails;
